import { useCallback, useEffect, useRef, useState } from "react";

const MODAL_TRANSITION_MS = 320;

export const useAuthModals = ({ initialModal = null, resetModal = null } = {}) => {
  const [activeModal, setActiveModal] = useState(initialModal);
  const modalTransitionTimeoutRef = useRef(null);
  const resetModalTimeoutRef = useRef(null);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const scheduleResetCallback = useCallback((callback) => {
    if (!callback) return;
    if (resetModalTimeoutRef.current) {
      clearTimeout(resetModalTimeoutRef.current);
    }
    resetModalTimeoutRef.current = setTimeout(() => {
      resetModalTimeoutRef.current = null;
      callback();
    }, MODAL_TRANSITION_MS);
  }, []);

  const showVerificationModal = useCallback(() => {
    if (modalTransitionTimeoutRef.current) {
      clearTimeout(modalTransitionTimeoutRef.current);
      modalTransitionTimeoutRef.current = null;
    }

    if (activeModal === "verify") return;

    const openVerify = () => {
      modalTransitionTimeoutRef.current = null;
      setActiveModal("verify");
    };

    if (activeModal) {
      setActiveModal(null);
      modalTransitionTimeoutRef.current = setTimeout(openVerify, MODAL_TRANSITION_MS);
    } else {
      openVerify();
    }
  }, [activeModal]);

  const handleCloseResetModal = useCallback(() => {
    closeModal();
    if (resetModal?.onClose) {
      scheduleResetCallback(resetModal.onClose);
    }
  }, [closeModal, resetModal, scheduleResetCallback]);

  const handleResetSuccess = useCallback(() => {
    closeModal();
    if (resetModal?.onSuccess) {
      scheduleResetCallback(resetModal.onSuccess);
    }
  }, [closeModal, resetModal, scheduleResetCallback]);

  useEffect(() => {
    if (initialModal) {
      setActiveModal(initialModal);
    }
  }, [initialModal]);

  useEffect(
    () => () => {
      if (resetModalTimeoutRef.current) {
        clearTimeout(resetModalTimeoutRef.current);
        resetModalTimeoutRef.current = null;
      }
      if (modalTransitionTimeoutRef.current) {
        clearTimeout(modalTransitionTimeoutRef.current);
        modalTransitionTimeoutRef.current = null;
      }
    },
    []
  );

  return {
    activeModal,
    openModal: setActiveModal,
    closeModal,
    isModalOpen: Boolean(activeModal),
    isRegisterModal: activeModal === "register",
    isVerificationModal: activeModal === "verify",
    showVerificationModal,
    handleCloseResetModal,
    handleResetSuccess,
  };
};
